import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Pencil, RefreshCw, Check, ChevronLeft, ChevronRight,
  Copy, GitBranch, RotateCcw, Pin, PinOff, MinusCircle,
} from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { renderContent } from "@/lib/chatConstants";

function ActionRow({ testId, onClick, icon, label }) {
  // Single row inside the long-press action sheet. Big touch target, hairline
  // divider between rows comes from the parent's `divide-y`.
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-white/[0.04] active:bg-white/[0.06] transition"
    >
      <span className="text-white/85 shrink-0">{icon}</span>
      <span className="text-base text-white">{label}</span>
    </button>
  );
}

export function MessageBubble({ message, character, streaming, onEdit, onRegenerate, onTogglePin, pinned, onSwitchVariant, onDelete, onFork, onRewind }) {
  const isUser = message.role === "user";
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(message.content || "");
  const [frozenWidth, setFrozenWidth] = useState(null);
  const [actionsOpen, setActionsOpen] = useState(false);
  const bubbleRef = useRef(null);
  const editTextareaRef = useRef(null);
  // Touch handling: swipe-left-to-regen AND long-press-to-open-actions on any bubble.
  const touchRef = useRef({ x: 0, y: 0, t: 0, timer: null, longPressFired: false });

  useEffect(() => { if (!editing) setDraft(message.content || ""); }, [message.content, editing]);

  useEffect(() => {
    // When the user enters edit mode, take control of scroll + focus so iOS
    // doesn't force the message into the center of the viewport (which hides
    // its bottom behind the on-screen keyboard). We park the bubble's TOP
    // just under the fixed header, then focus the textarea WITHOUT triggering
    // iOS's own scroll-into-view centering.
    if (!editing) {
      // Release the scroll-headroom reservation when exiting edit mode.
      document.documentElement.style.removeProperty("--edit-pad");
      return;
    }
    // Reserve a full viewport of extra bottom padding on the messages scroller
    // so even tall last-bubbles have enough headroom to be lifted under the
    // fixed header. Without this, scrollTo() clamps at maxScrollTop for the
    // last message and the bubble ends up mid-viewport.
    document.documentElement.style.setProperty("--edit-pad", "80vh");
    let raf = 0;
    let t = 0;
    const settle = () => {
      const ta = editTextareaRef.current;
      const bubble = bubbleRef.current;
      if (!ta || !bubble) return;
      // Find the nearest scrollable ancestor (the chat-messages scroller).
      let scroller = bubble.parentElement;
      while (scroller && scroller !== document.body) {
        const oy = getComputedStyle(scroller).overflowY;
        if (oy === "auto" || oy === "scroll") break;
        scroller = scroller.parentElement;
      }
      if (scroller && scroller !== document.body) {
        const bRect = bubble.getBoundingClientRect();
        const sRect = scroller.getBoundingClientRect();
        const HEADER = 76; // matches pt-[76px] on the messages scroller
        const target = (bRect.top - sRect.top) + scroller.scrollTop - HEADER - 8;
        scroller.scrollTo({ top: Math.max(0, target), behavior: "auto" });
      }
      // Focus without letting iOS re-scroll us.
      try { ta.focus({ preventScroll: true }); }
      catch { ta.focus(); }
      const end = ta.value.length;
      try { ta.setSelectionRange(end, end); } catch {}
    };
    // Give React two paints so the extra --edit-pad reservation takes effect
    // and the scroller's scrollHeight expands BEFORE we compute the target.
    raf = requestAnimationFrame(() => {
      requestAnimationFrame(() => { t = setTimeout(settle, 30); });
    });
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
      document.documentElement.style.removeProperty("--edit-pad");
    };
  }, [editing]);

  function beginEdit() {
    // Capture the current rendered width so the edit textarea reuses it.
    if (bubbleRef.current) {
      const w = bubbleRef.current.getBoundingClientRect().width;
      setFrozenWidth(Math.max(240, Math.round(w)));
    }
    setEditing(true);
  }

  function clearLongPress() {
    if (touchRef.current.timer) {
      clearTimeout(touchRef.current.timer);
      touchRef.current.timer = null;
    }
  }

  function onTouchStart(e) {
    if (editing) return;
    const t = e.touches?.[0]; if (!t) return;
    touchRef.current = {
      x: t.clientX, y: t.clientY, t: Date.now(), timer: null, longPressFired: false,
    };
    touchRef.current.timer = setTimeout(() => {
      // Fire only if the finger hasn't strayed (touchMove clears the timer).
      touchRef.current.longPressFired = true;
      // Small haptic buzz on supported devices.
      if (navigator.vibrate) { try { navigator.vibrate(15); } catch {} }
      setActionsOpen(true);
    }, 500);
  }
  function onTouchMove(e) {
    const t = e.touches?.[0]; if (!t) return;
    const dx = Math.abs(t.clientX - touchRef.current.x);
    const dy = Math.abs(t.clientY - touchRef.current.y);
    if (dx > 12 || dy > 12) clearLongPress();
  }
  function onTouchEnd(e) {
    clearLongPress();
    if (touchRef.current.longPressFired) return; // sheet already opened, don't process swipe
    const t = e.changedTouches?.[0]; if (!t) return;
    const dx = t.clientX - touchRef.current.x;
    const dy = t.clientY - touchRef.current.y;
    // Horizontal swipe on an assistant bubble navigates variants:
    //   swipe RIGHT (dx > 0) → previous variant  (like tapping the ‹ arrow)
    //   swipe LEFT  (dx < 0) → next variant      (like tapping the › arrow)
    // Falls back to regenerate if we're already at the newest variant.
    const isSwipe = Math.abs(dx) > 60 && Math.abs(dy) < 40;
    if (isSwipe && !isUser) {
      if (dx > 0) {
        // Swipe right → previous variant
        if (hasVariants && activeIdx > 0 && onSwitchVariant) {
          onSwitchVariant(message.id, activeIdx - 1);
        }
      } else {
        // Swipe left → next variant, or regenerate if at the tip
        if (hasVariants && activeIdx < variants.length - 1 && onSwitchVariant) {
          onSwitchVariant(message.id, activeIdx + 1);
        } else if (onRegenerate) {
          onRegenerate();
        }
      }
    }
  }
  function onTouchCancel() { clearLongPress(); }

  function onContextMenu(e) {
    // Desktop right-click opens the same action sheet as long-press.
    if (editing) return;
    e.preventDefault();
    setActionsOpen(true);
  }

  function saveEdit() {
    if (draft.trim() && draft !== message.content) onEdit && onEdit(draft.trim());
    setEditing(false);
  }

  async function copyContent() {
    try {
      await navigator.clipboard.writeText(message.content || "");
      toast.success("Copied.");
    } catch {
      toast.error("Copy failed.");
    }
  }

  // Variant navigation state for regenerated assistant messages.
  const variants = message.variants || [];
  const hasVariants = !isUser && variants.length > 1;
  const activeIdx = typeof message.active_variant === "number" ? message.active_variant : variants.length - 1;

  return (
    <div className={`fade-slide flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] flex flex-col ${isUser ? "items-end" : "items-start"}`}>
        <div
          ref={bubbleRef}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onTouchCancel={onTouchCancel}
          onContextMenu={onContextMenu}
          style={editing && frozenWidth ? { width: `${frozenWidth}px` } : undefined}
          className={`rounded-2xl px-4 py-3 chat-content leading-relaxed select-text ${
            isUser
              ? "bg-[#2A2836] text-white rounded-br-sm"
              : "bg-[#1B1826] text-neutral-100 rounded-bl-sm border border-white/5"
          }`}
        >
          {!isUser && (
            <div className="mb-1 flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-violet-300/80">{character.name}</span>
            </div>
          )}

          {editing ? (
            <div>
              <textarea
                data-testid={`msg-edit-textarea-${message.id}`}
                value={draft}
                onChange={(e) => {
                  setDraft(e.target.value);
                  // Auto-grow so the full message is always visible while editing.
                  const el = e.target;
                  const prevH = el.offsetHeight;
                  el.style.height = "auto";
                  el.style.height = Math.min(el.scrollHeight, 420) + "px";
                  // Keep the caret visible: if the textarea grew this keystroke,
                  // nudge the parent scroller by the same delta so the bottom
                  // of the textarea stays where it was on screen (i.e. above
                  // the keyboard / composer).
                  const grew = el.offsetHeight - prevH;
                  if (grew > 0) {
                    let scroller = el.parentElement;
                    while (scroller && scroller !== document.body) {
                      const oy = getComputedStyle(scroller).overflowY;
                      if (oy === "auto" || oy === "scroll") break;
                      scroller = scroller.parentElement;
                    }
                    if (scroller && scroller !== document.body) {
                      scroller.scrollTop += grew;
                    }
                  }
                }}
                ref={(el) => {
                  editTextareaRef.current = el;
                  // Grow to fit the initial content on mount.
                  if (el && !el.dataset.sized) {
                    el.style.height = "auto";
                    el.style.height = Math.min(el.scrollHeight, 420) + "px";
                    el.dataset.sized = "1";
                  }
                }}
                className="w-full resize-none rounded-xl border border-violet-500/30 bg-white/5 px-3 py-2 text-white outline-none focus:border-violet-500/60 font-body text-sm min-h-[96px] max-h-[420px] overflow-y-auto leading-relaxed"
              />
              <div className="mt-2 flex gap-2">
                <button data-testid={`msg-edit-save-${message.id}`} onClick={saveEdit} className="btn-pill btn-primary text-xs px-3 py-1.5"><Check size={12} /> Save</button>
                <button data-testid={`msg-edit-cancel-${message.id}`} onClick={() => { setEditing(false); setDraft(message.content); }} className="btn-pill btn-ghost text-xs px-3 py-1.5">Cancel</button>
              </div>
            </div>
          ) : (
            <div
              className="chat-content"
              dangerouslySetInnerHTML={{ __html: renderContent(message.content || "") }}
            />
          )}
          {streaming && (
            <span className="inline-block ml-1 h-3 w-2 translate-y-0.5 animate-pulse bg-violet-400" />
          )}
          {message.edited_at && !editing && (
            <div className="mt-1 text-[9px] uppercase tracking-widest text-neutral-500">edited</div>
          )}
        </div>

        {/* Action row BELOW the bubble — only shows the primary Regenerate
            CTA now. Edit lives in the long-press action sheet (Copy / Pin /
            Rewind / Remove / Edit) to keep the reading surface uncluttered. */}
        {!editing && onRegenerate && !streaming && (
          <div className={`mt-1.5 flex items-center gap-1.5 ${isUser ? "justify-end" : "justify-start"}`}>
            <button
              onClick={onRegenerate}
              data-testid={`msg-regen-${message.id}`}
              className="inline-flex items-center gap-1 rounded-full bg-violet-500/15 border border-violet-500/30 px-2.5 py-1 text-[11px] text-violet-200 hover:bg-violet-500/25 transition"
              aria-label="Regenerate response"
              title="Regenerate (or swipe left on mobile)"
            >
              <RefreshCw size={11} /> Regenerate
            </button>
          </div>
        )}

        {/* Variant navigator — only visible when this bot message has been regenerated
            at least once. Lets the user swipe back and forth through past variants. */}
        {hasVariants && !editing && !streaming && (
          <div className="mt-1 flex items-center gap-1.5" data-testid={`msg-variants-${message.id}`}>
            <button
              type="button"
              data-testid={`msg-variant-prev-${message.id}`}
              disabled={activeIdx <= 0}
              onClick={() => onSwitchVariant && onSwitchVariant(message.id, activeIdx - 1)}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/5 border border-white/10 text-neutral-300 hover:bg-white/10 hover:text-white transition disabled:opacity-30 disabled:hover:bg-white/5"
              aria-label="Previous variant"
            >
              <ChevronLeft size={13} />
            </button>
            <span className="text-[10px] font-mono tabular-nums text-neutral-400" data-testid={`msg-variant-index-${message.id}`}>
              {activeIdx + 1} / {variants.length}
            </span>
            <button
              type="button"
              data-testid={`msg-variant-next-${message.id}`}
              disabled={activeIdx >= variants.length - 1}
              onClick={() => onSwitchVariant && onSwitchVariant(message.id, activeIdx + 1)}
              className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/5 border border-white/10 text-neutral-300 hover:bg-white/10 hover:text-white transition disabled:opacity-30 disabled:hover:bg-white/5"
              aria-label="Next variant"
            >
              <ChevronRight size={13} />
            </button>
          </div>
        )}
      </div>

      {/* Long-press / right-click actions Sheet */}
      <Sheet open={actionsOpen} onOpenChange={setActionsOpen}>
        <SheetContent
          side="bottom"
          hideCloseButton
          className="border-0 bg-transparent shadow-none px-3 pb-3 pt-0"
          data-testid={`msg-actions-sheet-${message.id}`}
        >
          {/* Screen-reader only header (visually hidden per shadcn a11y contract) */}
          <SheetHeader className="sr-only">
            <SheetTitle>Message actions</SheetTitle>
            <SheetDescription>
              {isUser ? "Your message" : `${character.name}'s message`}
            </SheetDescription>
          </SheetHeader>

          {/* Unified card — mirrors the mock exactly: one rounded rectangle,
              hairline dividers between rows, big touch targets. */}
          <div className="mx-auto max-w-md overflow-hidden rounded-2xl bg-[#1B1826] border border-white/5 divide-y divide-white/5">
            <ActionRow
              testId={`msg-action-copy-${message.id}`}
              onClick={() => { copyContent(); setActionsOpen(false); }}
              icon={<Copy size={20} />}
              label="Copy"
            />
            {onEdit && (
              <ActionRow
                testId={`msg-action-edit-${message.id}`}
                onClick={() => { setActionsOpen(false); beginEdit(); }}
                icon={<Pencil size={20} />}
                label="Edit"
              />
            )}
            {onFork && (
              <ActionRow
                testId={`msg-action-fork-${message.id}`}
                onClick={() => { setActionsOpen(false); onFork(); }}
                icon={<GitBranch size={20} />}
                label="New chat from here"
              />
            )}
            {onRewind && (
              <ActionRow
                testId={`msg-action-rewind-${message.id}`}
                onClick={() => { setActionsOpen(false); onRewind(); }}
                icon={<RotateCcw size={20} />}
                label="Rewind to here"
              />
            )}
            {onTogglePin && (
              <ActionRow
                testId={`msg-action-pin-${message.id}`}
                onClick={() => { onTogglePin(); setActionsOpen(false); }}
                icon={pinned ? <PinOff size={20} /> : <Pin size={20} />}
                label={pinned ? "Unpin" : "Pin"}
              />
            )}
            {onDelete && (
              <ActionRow
                testId={`msg-action-delete-${message.id}`}
                onClick={() => { setActionsOpen(false); onDelete(); }}
                icon={<MinusCircle size={20} />}
                label="Remove"
              />
            )}
          </div>

          <button
            data-testid={`msg-action-close-${message.id}`}
            onClick={() => setActionsOpen(false)}
            className="mx-auto mt-3 flex w-full max-w-md items-center justify-center rounded-2xl bg-[#1B1826] border border-white/5 py-4 text-base font-medium text-white hover:bg-[#241F30] transition"
          >
            Close
          </button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
